import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/apiFetch";
import { Button } from "@/components/ui/button";

type Source = { document_id: number; filename: string; quote: string; support: string; explanation: string;
  source_kind: string; current: boolean; page_number: number | null; url: string | null };
type Result = { job: null | { status: string; error_message: string | null }; completed: number; total: number;
  entries?: { data: { id: number; ordinal: number; statement: string; status: string; sources: Source[] }[]; last_page: number } };

export default function ReportSources({ reportId }: { reportId: number }) {
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const next = await apiFetch<Result>(`/api/project-analyses/${reportId}/sources?page=${page}`);
        if (stopped) return;
        setResult(next);
        setError("");
        if (next.job && ["queued", "processing"].includes(next.job.status)) timer = setTimeout(load, 5000);
      } catch (e) { if (!stopped) setError(e instanceof Error ? e.message : "Could not load sources"); }
    };
    load();
    return () => { stopped = true; clearTimeout(timer); };
  }, [reportId, page, revision]);
  const start = async () => {
    setBusy(true); setError("");
    try {
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      await apiFetch(`/api/project-analyses/${reportId}/sources`, { method: "POST", body: { refresh: result?.job?.status === "completed" } });
      setPage(1); setRevision(v => v + 1);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not start source lookup"); }
    finally { setBusy(false); }
  };
  const running = !!result?.job && ["queued", "processing"].includes(result.job.status);
  return <section className="border-t pt-4 space-y-3">
    <h3 className="font-semibold">Report sources</h3>
    <p className="text-sm text-muted-foreground">Optional search of current stored project text. This adds source excerpts without changing the answer and uses additional AI processing. Quotes are matched exactly; support assessments are AI-assisted. The search may miss supporting documents, and a missing match does not prove a statement false.</p>
    <Button variant="outline" onClick={start} disabled={busy || running || !result}>
      {running ? "Finding sources…" : result?.job?.status === "completed" ? "Recheck sources" : result?.job?.status === "failed" ? "Retry source lookup" : "Find sources for this report"}
    </Button>
    {error && <p role="alert" className="text-red-600">{error}</p>}
    {error && <Button variant="outline" onClick={() => setRevision(v => v + 1)}>Reload source status</Button>}
    {result?.job && <p className="text-sm">{result.job.status} · {result.completed}/{result.total} entries checked{result.job.status === "queued" ? ". Waiting for the analysis worker." : ""}</p>}
    {result?.job?.error_message && <p role="alert" className="text-red-600">{result.job.error_message}</p>}
    {result?.entries?.data.map(entry => <details key={entry.id} className="border rounded p-3">
      <summary className="cursor-pointer">{entry.statement.split("\n")[0]} — {entry.status !== "completed" ? "Pending" : entry.sources.some(s => s.current) ? `${entry.sources.filter(s => s.current).length} source matches` : "Source not verified"}</summary>
      <p className="whitespace-pre-wrap text-sm mt-2">{entry.statement}</p>
      {entry.status === "completed" && !entry.sources.length && <p className="text-sm mt-2">Source not verified in the candidate passages searched.</p>}
      {entry.sources.map((source, index) => <div key={index} className="border-t mt-3 pt-3 space-y-1 text-sm">
        {source.url ? <a className="text-primary underline" href={source.url} target="_blank" rel="noreferrer">Open / download {source.filename}</a> : <p>{source.filename} — recheck sources.</p>}
        {source.page_number !== null && <p>Stored page reference: {source.page_number} (may differ from the PDF viewer page)</p>}
        <p>{source.support === "partial" ? "Partial support" : "Supports this entry"} · {source.source_kind.replaceAll("_", " ")} (AI assessment)</p>
        <blockquote className="border-l-2 pl-3 whitespace-pre-wrap">{source.quote}</blockquote>
        <p>{source.explanation}</p>
      </div>)}
    </details>)}
    {(result?.entries?.last_page || 0) > 1 && <div className="flex gap-3 items-center">
      <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous sources</Button>
      <span>Page {page} of {result?.entries?.last_page}</span>
      <Button variant="outline" disabled={page >= (result?.entries?.last_page || 1)} onClick={() => setPage(p => p + 1)}>Next sources</Button>
    </div>}
  </section>;
}
